import { format } from "date-fns";
import { Lead } from "@/context/LeadContext";
import { Partner } from "@/context/PartnerContext";

export const LEAD_PRICE = 3;

export type BillingPeriod = "week" | "month" | "quarter" | "year" | "all" | "custom";

export interface PartnerBillingRow {
  partner: Partner;
  leadCount: number;
  amount: number;
  leads: Lead[];
}

export interface AllPartnersBilling {
  rows: PartnerBillingRow[];
  totalLeads: number;
  totalAmount: number;
}

export interface MonthlyBillingPoint {
  month: string;
  count: number;
  amount: number;
}

/**
 * The minimum a lead needs for the billing maths.
 *
 * The partner portal builds its own trimmed lead objects from
 * get-partner-leads.php, so requiring the full `Lead` here forced a type
 * mismatch — and, because that trimmed shape has no `partnerId` at all, the
 * `partnerId !== null` guard below was passing on `undefined` by accident
 * rather than by intent.
 */
export interface BillableLead {
  id: string;
  name: string;
  service: string;
  createdAt: string;
  partnerId?: string | null;
}

// ── Date helpers ──────────────────────────────────────────────────────────────

/**
 * Extract "YYYY-MM-DD" from a MySQL datetime string without any timezone conversion.
 * MySQL stores dates in server local time (Greece UTC+3), so we treat them as-is.
 */
function leadDateStr(lead: BillableLead): string {
  return lead.createdAt.slice(0, 10).replace("T", " ").slice(0, 10);
}

/** Format a JS Date as "YYYY-MM-DD" in local time */
function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/** Today's date at midnight local time */
function today(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Get the start Date for a named period (local midnight) */
export function getStartDate(period: Exclude<BillingPeriod, "all" | "custom">): Date {
  const now = today();
  switch (period) {
    case "week": {
      const day = now.getDay(); // 0=Sun
      const offset = day === 0 ? -6 : 1 - day; // back to Monday
      return new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
    }
    case "month":
      return new Date(now.getFullYear(), now.getMonth(), 1);
    case "quarter": {
      const q = Math.floor(now.getMonth() / 3);
      return new Date(now.getFullYear(), q * 3, 1);
    }
    case "year":
      return new Date(now.getFullYear(), 0, 1);
  }
}

// ── Filter helpers ────────────────────────────────────────────────────────────

/**
 * Filter assigned leads by date range using string comparison (YYYY-MM-DD).
 * Both from and to are inclusive. Pass null to skip that boundary.
 */
export function filterLeadsByDateRange<T extends BillableLead>(
  leads: T[],
  from: Date | null,
  to: Date | null
): T[] {
  // `!= null` catches both null and undefined: the partner portal's lead shape
  // omits partnerId entirely, and those leads are already scoped to one partner.
  const assigned = leads.filter((l) => l.partnerId !== null);
  const fromStr = from ? toDateStr(from) : null;
  const toStr   = to   ? toDateStr(to)   : null;
  return assigned.filter((l) => {
    const d = leadDateStr(l);
    if (fromStr && d < fromStr) return false;
    if (toStr   && d > toStr)   return false;
    return true;
  });
}

export function filterLeadsByPeriod<T extends BillableLead>(
  leads: T[],
  period: BillingPeriod,
  customFrom?: Date | null,
  customTo?: Date | null
): T[] {
  if (period === "all") {
    return leads.filter((l) => l.partnerId !== null);
  }
  if (period === "custom") {
    return filterLeadsByDateRange(leads, customFrom ?? null, customTo ?? null);
  }
  const startDate = getStartDate(period);
  return filterLeadsByDateRange(leads, startDate, today());
}

// ── Billing computation ───────────────────────────────────────────────────────

export function computePartnerBilling(
  leads: Lead[],
  partner: Partner,
  period: BillingPeriod,
  customFrom?: Date | null,
  customTo?: Date | null
): PartnerBillingRow {
  const periodLeads = filterLeadsByPeriod(leads, period, customFrom, customTo).filter(
    (l) => l.partnerId === partner.id
  );
  return {
    partner,
    leadCount: periodLeads.length,
    amount: periodLeads.length * LEAD_PRICE,
    leads: periodLeads,
  };
}

export function computeAllPartnersBilling(
  leads: Lead[],
  partners: Partner[],
  period: BillingPeriod,
  customFrom?: Date | null,
  customTo?: Date | null
): AllPartnersBilling {
  const rows: PartnerBillingRow[] = partners
    .filter((p) => p.active)
    .map((p) => computePartnerBilling(leads, p, period, customFrom, customTo))
    .sort((a, b) => b.leadCount - a.leadCount);

  const totalLeads = rows.reduce((s, r) => s + r.leadCount, 0);
  const totalAmount = totalLeads * LEAD_PRICE;
  return { rows, totalLeads, totalAmount };
}

// ── Chart data ────────────────────────────────────────────────────────────────

export function groupLeadsByMonth<T extends BillableLead>(leads: T[]): MonthlyBillingPoint[] {
  const assigned = leads.filter((l) => l.partnerId !== null);
  const map = new Map<string, number>();

  for (const lead of assigned) {
    const key = lead.createdAt.slice(0, 7); // "YYYY-MM"
    map.set(key, (map.get(key) ?? 0) + 1);
  }

  const result: MonthlyBillingPoint[] = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = format(d, "MMM yyyy");
    const count = map.get(key) ?? 0;
    result.push({ month: label, count, amount: count * LEAD_PRICE });
  }
  return result;
}

const GREEK_MONTHS: Record<string, string> = {
  Jan: "Ιαν", Feb: "Φεβ", Mar: "Μαρ", Apr: "Απρ", May: "Μάι",
  Jun: "Ιουν", Jul: "Ιουλ", Aug: "Αυγ", Sep: "Σεπ", Oct: "Οκτ",
  Nov: "Νοε", Dec: "Δεκ",
};

export function toGreekMonth(label: string): string {
  const [mon, yr] = label.split(" ");
  return (GREEK_MONTHS[mon] ?? mon) + " " + yr;
}

// ── CSV Export ────────────────────────────────────────────────────────────────

export function exportBillingCSV(
  rows: PartnerBillingRow[],
  period: BillingPeriod,
  customFrom?: Date | null,
  customTo?: Date | null
): void {
  const periodLabel: Record<BillingPeriod, string> = {
    week: "Εβδομάδα", month: "Μήνας", quarter: "Τρίμηνο",
    year: "Έτος", all: "Όλα",
    custom: customFrom && customTo
      ? `${toDateStr(customFrom)} – ${toDateStr(customTo)}`
      : "Προσαρμοσμένο",
  };
  const headers = ["Συνεργάτης", "Leads", "Ποσό (€)", "Περίοδος"];
  const csvRows = [
    headers.join(","),
    ...rows.map((r) =>
      [`"${r.partner.name}"`, r.leadCount, r.amount, periodLabel[period]].join(",")
    ),
    ["ΣΥΝΟΛΟ", rows.reduce((s, r) => s + r.leadCount, 0), rows.reduce((s, r) => s + r.amount, 0), ""].join(","),
  ];

  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `χρεωσεις_${period}_${format(new Date(), "yyyy-MM-dd")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
